import CloseIcon from "@mui/icons-material/Close";
import { Box, ClickAwayListener, IconButton, Popper, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import * as calendarTaskService from "source/models/services/calendarTaskService";
import { ToolButtonDropdown } from "ui/Scripts/source/components/framework/Button/ToolButtonDropdown";
import { Translations } from "ui/Scripts/source/models/translations";
import { DangerButton } from "../../Button/DangerButton";
import { EllipsisTypography } from "../../Typography/EllipsisTypography";
import { MultirowEllipsisTypography } from "../../Typography/MultirowEllipsisTypography";
import { ICalendarItem } from "../Calendar";
import { AppUtils } from "source/framework/appUtils";
import { AppButton } from "ui/Scripts/source/components/framework/Button/AppButton";

interface CalendarItemDetailPopupProps {
    anchorEl?: HTMLElement;
    event: ICalendarItem;
    onOpenDialog: (id: string) => void;
    onRemoveItem: (id: string) => void;
    onClose: () => void;
}

export const CalendarItemDetailPopup = (props: CalendarItemDetailPopupProps) => {
    const [state, setState] = useState({
        isLoading: true,
        canSaveCalendarTask: false,
        canRemoveCalendarTask: false,
    });

    const handleClickAway = (event: MouseEvent | TouchEvent) => {
        event.stopPropagation();
        event.stopImmediatePropagation();
        props.onClose();
    };

    useEffect(() => {
        const loadData = async () => {
            const result = await AppUtils.callService(() => calendarTaskService.getCalendarTaskPermissions(props.event.companyId, props.event.id, props.event.recordId, props.event.databaseRecordType), false, false, () => setState((old) => ({ ...old, isLoading: false })));
            if (!result) return;
            setState((old) => ({
                ...old,
                isLoading: false,
                canSaveCalendarTask: result.canSaveCalendarTask,
                canRemoveCalendarTask: result.canRemoveCalendarTask
            }));
        };

        loadData();
    }, [props.event.companyId, props.event.id]);
    
    if (state.isLoading) return null;

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <Popper
                sx={{
                    position: "fixed",
                    border: "1px solid",
                    borderRadius: "4px",
                    bgcolor: "background.paper",
                    zIndex: 999,
                    width: "280px",
                    overflow: "hidden",
                }}
                open
                anchorEl={props.anchorEl}
            >
                <Box
                    sx={{
                        bgcolor: "background.default",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ marginLeft: ".25rem" }}
                    >
                        {Translations.CalendarTask}
                    </Typography>
                    <IconButton onClick={props.onClose} size="small">
                        <CloseIcon sx={{ fontSize: ".75rem" }} />
                    </IconButton>
                </Box>
                <Box
                    sx={{
                        bgcolor: props.event?.color,
                        marginBottom: ".25rem",
                        height: "21px"
                    }}
                >
                    <EllipsisTypography sx={{ marginLeft: ".25rem" }}>{props.event?.title}</EllipsisTypography>
                </Box>
                <Typography color="text.secondary" sx={{ marginLeft: "1rem", marginRight: ".25rem" }}>
                    {props.event?.formatedDate}
                </Typography>
                <MultirowEllipsisTypography
                    color="text.secondary"
                    maxRows={2}
                    sx={{
                        height: "41px",
                        margin: "0 1rem 1.25rem 1rem",
                    }}
                >
                    {props.event?.location}
                </MultirowEllipsisTypography>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        padding: ".5rem"
                    }}
                >
                    {props.event?.parentId &&
                        <>
                            <ToolButtonDropdown
                                variant="danger"
                                disabled={!state.canRemoveCalendarTask}
                                actions={[
                                    { title: Translations.CalendarTaskRemoveOccurrence, onClick: () => props.onRemoveItem(props.event?.id) },
                                    { title: Translations.CalendarTaskRemoveSeries, onClick: () => props.onRemoveItem(props.event?.parentId) },
                                ]}
                                margin="0 1rem 0 0"
                                title={Translations.Remove}
                                menuRight={true}
                                size="small"
                            />
                            <ToolButtonDropdown
                                variant="contained"
                                
                                actions={[
                                    { title: state.canSaveCalendarTask ? Translations.CalendarTaskEditOccurrence : Translations.CalendarTaskOpenOccurrence, onClick: () => props.onOpenDialog(props.event?.id) },
                                    { title: state.canSaveCalendarTask ? Translations.CalendarTaskEditSeries : Translations.CalendarTaskOpenSeries, onClick: () => props.onOpenDialog(props.event?.parentId) },
                                ]}
                                title={Translations.Edit}
                                menuRight={true}
                                size="small"
                            />
                        </>
                    }
                    {!props.event?.parentId &&
                        <>
                            <DangerButton
                                disabled={!state.canRemoveCalendarTask}
                                onClick={() => props.onRemoveItem(props.event?.id)}
                                size="small"
                                sx={{ marginRight: ".25rem" }}
                            >
                                {Translations.Remove}
                            </DangerButton>
                            <AppButton variant="contained" size="small" onClick={() => props.onOpenDialog(props.event?.id)}>
                                {state.canSaveCalendarTask ? Translations.Edit : Translations.Open}
                            </AppButton>
                        </>
                    }
                </Box>
            </Popper>
        </ClickAwayListener>
    );
};
